import { FC, useMemo } from 'react'
import { useUA } from 'context'
import { useRouter } from 'next/router'
import styled, { css, DefaultTheme } from 'styled-components'
import {
  EXAM_STAGE_TAB,
  EXAM_TAB_OPTIONS,
  TExamTab,
} from '@app.feature/exam/constants/tabs'
import useGetIsPreReserved from '@app.feature/exam/hooks/useGetIsPreReserved'
import ExamTabTooltipDesktop from '../Tooltip/ExamTabTooltipDesktop'
import ExamTabTooltipMobile from '../Tooltip/ExamTabTooltipMobile'

interface IFProps {
  tab: TExamTab
  activeTab: string | null
  companyId: string
  termId: string
}

const ExamTab: FC<IFProps> = ({ tab, activeTab, companyId, termId }) => {
  const router = useRouter()
  const { isMobile } = useUA()
  const isShowTooltipPosition =
    tab.id == EXAM_STAGE_TAB.PREDICTION_SCORE &&
    activeTab !== EXAM_STAGE_TAB.EXAM_VOTE

  const { pathname, query } = router
  const iconImageSrc = '/static/images/exam/hand-blue.webp'

  const isPreReserved = useGetIsPreReserved({
    companyId,
    termId,
  })

  const preReservedText =
    tab.id === 'full-reservation'
      ? isMobile
        ? `풀서비스\n신청하기`
        : '풀서비스 신청하기'
      : tab.name

  const handleClickTab = () => {
    router.replace(
      {
        pathname,
        query: {
          ...query,
          tab: tab.id,
        },
        hash: tab.sectionId,
      },
      undefined,
      { shallow: true },
    )
  }

  const isShowImage = useMemo(() => {
    if (activeTab) {
      const currentIndex = EXAM_TAB_OPTIONS.findIndex(
        (option) => option.id === activeTab,
      )
      const nextIndex = currentIndex + 1
      return EXAM_TAB_OPTIONS[nextIndex]?.id === tab.id
    } else {
      return tab.id === EXAM_TAB_OPTIONS[0].id
    }
  }, [activeTab, tab.id])

  return (
    <StyledWrapper data-active={activeTab === tab.id} onClick={handleClickTab}>
      <div>{tab.label}</div>
      <span>{isPreReserved ? tab.name : preReservedText}</span>
      {isShowImage && <img src={iconImageSrc} alt="click" />}
      {isShowTooltipPosition ? (
        isMobile ? (
          <ExamTabTooltipMobile />
        ) : (
          <ExamTabTooltipDesktop />
        )
      ) : null}
    </StyledWrapper>
  )
}

export default ExamTab

const desktopStyles = (theme: DefaultTheme) => css`
  position: relative;
  width: 212px;
  height: 110px;
  margin: 12px 0;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 24px 18px;
  background-color: ${theme.background.bg_8};
  border-radius: 10px;
  border: 1px solid #eeeeee;
  box-shadow: 0 2px 5px 0 rgba(102, 102, 102, 0.16);
  color: ${theme.color.text_1};
  font-weight: bold;
  transition: all 0.2s ease-in-out;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 8px 0 rgba(102, 102, 102, 0.25);
    border-color: ${theme.color.primary_2};
  }

  &[data-active='true'] {
    background-color: ${theme.color.primary_2};
    color: ${theme.background.bg_8};

    > div {
      color: ${theme.background.bg_8};
    }
  }

  > div {
    font-size: 18px;
    letter-spacing: -0.72px;
    padding: 2px 12px;
    border: 1px solid ${theme.line.line_2};
    border-radius: 15px;
    margin-bottom: 7px;
    color: #777777;
  }
  > span {
    font-size: 22px;
    letter-spacing: -0.88px;
  }
`

const mobileStyles = (theme: DefaultTheme) => css`
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: space-around;
  align-items: center;
  padding: 8px 6px;
  border-right: 1px solid #dddddd;

  &[data-active='true'] {
    background-color: ${theme.color.primary_2};
    color: ${theme.background.bg_8};

    > div {
      color: ${theme.background.bg_8};
    }
  }

  > img {
    position: absolute;
    top: 80px;
    width: 64px;
    height: 40px;
    z-index: 10;
  }

  > div {
    width: fit-content;
    border: 1px solid ${theme.line.line_2};
    border-radius: 9px;
    padding: 1px 7px;
    white-space: nowrap;
    font-size: 10px;
    letter-spacing: -0.4px;
    font-weight: bold;
    color: ${theme.color.text_1};
  }
  > span {
    white-space: pre;
    font-size: 14px;
    font-weight: bold;
    letter-spacing: -0.56px;
    text-align: center;
  }
`

const StyledWrapper = styled.li`
  ${({ theme }) => css`
    ${theme.isMobile ? mobileStyles(theme) : desktopStyles(theme)}
  `}
`
